import React, {useEffect, useMemo, useRef} from 'react';
import DataGrid, {Column, Editing} from 'devextreme-react/data-grid';
import {Item as TabItem} from "devextreme-react/tab-panel";
import Form, {FormRef, GroupItem, Label, SimpleItem} from "devextreme-react/form";
import IEtatFinancierGroupe from "../../models/IEtatFinancierGroupe";
import {FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar} from '../base';
import {CheckBox, SelectBox} from 'devextreme-react';
import useCustomDataSource, {Mode} from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import polices from './polices.json';

export default function GroupesEtatFinancier() {
    const etatFinancierFormDataSource = useCustomDataSource<IEtatFinancierGroupe, IEtatFinancierGroupe>(
        {
            path: "etatFinancierGroupe",
            primaryKey: "idEtatFinancierGroupe",
            customReadByIdPath: "etatFinancierGroupe/{key}/gestion",
            customSauvegarderPath: "etatFinancierGroupe/save"
        });
    const formRef = useRef<FormRef>(null);
    const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);
    const {setMainForm, setSelectedDataRow, selectedDataRow: selectedGroupe, mode, setMode, isFieldModifier, setIsFieldModifier, onFieldDataChanged} = etatFinancierFormDataSource;

    const descriptionOptions = useMemo(() => {
        return {maxLength: 100};
    }, []);

    const typeCompteOptions = useMemo(() => {
        return {maxLength: 2};
    }, []);

    const groupeEditorOption = useMemo(() => {
        return {
            dataSource: ["Bilan"],
            layout: "horizontal"
        };
    }, []);

    const tailleEditorOptions = useMemo(() => {
        return {defaultValue: 0.00, min: 0.00, width: '100px'}
    }, []);

    const items = [
        {id: 1, text: 'Arial Text', font: 'Arial, sans-serif'},
        {id: 2, text: 'Courier New Text', font: '"Courier New", Courier, monospace'},
        {id: 3, text: 'Georgia Text', font: 'Georgia, serif'},
        {id: 4, text: 'Times New Roman Text', font: '"Times New Roman", Times, serif'},
    ];

    const policeEditorOptions = useMemo(() => {
        return {
            dataSource: items,
            // displayExpr: 'text', // Text displayed in the dropdown
            // valueExpr: 'id', // Value field
            itemRender: (item: any) => (
                <div
                    style={{fontFamily: item.font, color: 'red'}}
                >
                    {item.text}
                </div>
            ),
        }
    }, []);

    const radioEditorOptions = useMemo(() => {
        return {
            dataSource: ["Aucun", "Simple", "Double"],
            layout: "vertical"
        }
    }, []);

    useEffect(() => {
        setMainForm(formRef.current?.instance());
    })

    useEffect(() => {
        if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
    }, [mode])

    const typesComptes = ["Actif", "Inactif"];
    return (
        <FormListBase key="frm-group-etat-financier" data={etatFinancierFormDataSource}>
            <FormListBaseGrid dataSource={etatFinancierFormDataSource.dataSource}
                              onFocusedRowChanged={() => {
                                  if (mode === Mode.Modifier) setMode(Mode.Lecture)
                              }}>
                <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false}/>
            </FormListBaseGrid>
            <FormListBaseToolbar headerText={`${selectedGroupe?.description || ''}`} ajouterOptions={{disabled: !isFieldModifier}} sauvegarderOptions={{disabled: !isFieldModifier}} annulerOptions={{disabled: !isFieldModifier}}/>
            <FormListBaseTab>
                <TabItem title="Informations">
                    <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedGroupe || {} as IEtatFinancierGroupe} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                        <GroupItem>
                            <GroupItem>
                                <SimpleItem key={"frm-description"} dataField='description' editorOptions={descriptionOptions}>
                                    <Label text={"Description"}/>
                                </SimpleItem>
                                <SimpleItem key={"frm-appartenance-groupe"} editorType={"dxSelectBox"} dataField={"idParent"} editorOptions={groupeEditorOption}>
                                    <Label text={"Ce groupe appartient à"}/>
                                </SimpleItem>
                                <SimpleItem key={"frm-types-comptes"} editorOptions={typeCompteOptions}>
                                    <Label text={"Types de comptes"}/>
                                    <SelectBox items={typesComptes} defaultValue={typesComptes[0]}/>
                                </SimpleItem>
                                <SimpleItem key={"frm-groupe-total"}>
                                    <Label text={"Groupe avec total"}/>
                                    <CheckBox value={true}/>
                                </SimpleItem>
                            </GroupItem>
                            <GroupItem colCount={3}>
                                <GroupItem caption={"Souligné"}>
                                    <GroupItem colCount={2}>
                                        <GroupItem caption='Avant'>
                                            <SimpleItem key={"frm-avant"} dataField={"totalSoulignerAvant"} editorType={"dxRadioGroup"} editorOptions={radioEditorOptions}>
                                                <Label visible={false}/>
                                            </SimpleItem>
                                        </GroupItem>
                                        <GroupItem caption='Après'>
                                            <SimpleItem key={"frm-apres"} dataField={"totalSoulignerApres"} editorType={"dxRadioGroup"} editorOptions={radioEditorOptions}>
                                                <Label visible={false}/>
                                            </SimpleItem>
                                        </GroupItem>
                                    </GroupItem>
                                </GroupItem>
                                <GroupItem colSpan={2} caption={"Police"}>
                                    <GroupItem colCount={2}>
                                        {/* <SimpleItem
                      key={"frm-police"}
                      editorType="dxSelectBox"
                      dataField={"nomPolice"}
                      editorOptions={policeEditorOptions}
                    >
                      <Label text={"Police"} />
                    </SimpleItem> */}
                                        <SimpleItem>
                                            <SelectBox
                                                dataSource={polices}
                                                displayExpr="name" // Field shown in the input after selection
                                                valueExpr="id" // Field used as the actual value (optional)
                                                itemRender={(item) => (
                                                    <div style={{fontFamily: item.fontFamily}}>
                                                        {item.name}
                                                    </div>
                                                )}
                                            />
                                        </SimpleItem>
                                        <SimpleItem key={"frm-taille"} dataField={"taillePolice"} editorOptions={tailleEditorOptions}>
                                            <Label text={"Taille"}/>
                                        </SimpleItem>
                                    </GroupItem>
                                    <GroupItem>
                                        <SimpleItem key={"frm-gras"} dataField={"gras"} editorType={"dxCheckBox"}>
                                            <Label alignment='right' showColon={false}/>
                                        </SimpleItem>
                                        <SimpleItem key={"frm-italique"} dataField={"italique"} editorType={"dxCheckBox"}>
                                            <Label alignment='right' showColon={false}/>
                                        </SimpleItem>
                                        <SimpleItem key={"frm-souligne"} dataField={"souligne"} editorType={"dxCheckBox"}>
                                            <Label text='Souligné' alignment='right' showColon={false}/>
                                        </SimpleItem>
                                    </GroupItem>
                                </GroupItem>
                            </GroupItem>
                            <GroupItem>
                                <DataGrid
                                    // keyExpr="idFolio"
                                    // dataSource={selectedJournal.journalDetail}
                                    showBorders={true}
                                    allowColumnResizing={true}
                                    dataSource={selectedGroupe.annexes}
                                    allowColumnReordering={false}
                                    filterValue={dataGridFilterValue}
                                    height={"100%"}
                                    onInitNewRow={(e: any) => {
                                        e.data.idEtatFinancierGroupe = selectedGroupe.idEtatFinancierGroupe;
                                        e.data.isDeleted = false;
                                    }}
                                    onEditorPreparing={(e: any) => {
                                        const originalOnValueChanged = e.editorOptions.onValueChanged;
                                        e.editorOptions.onValueChanged = (args: any) => {
                                            if (originalOnValueChanged) {
                                                originalOnValueChanged(args);
                                            }

                                            const currentData = e.component.option("dataSource");
                                            const rowIndex = e.rowIndex;
                                            const rowData = currentData[rowIndex];

                                            const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                                            setSelectedDataRow(prev => ({
                                                ...prev,
                                                annexes: updatedData
                                            }));
                                            setIsFieldModifier(true);
                                        }
                                    }}
                                    onRowRemoving={async (e) => {
                                        e.cancel = true;
                                        // if (e?.data?.idPermisType) {
                                        const index = selectedGroupe.annexes.findIndex(item =>
                                            item.idEtatFinancierGroupe.equals(e.data.idEtatFinancierGroupe)
                                        );
                                        if (index !== -1) {
                                            const updatedData = [...selectedGroupe.annexes];
                                            updatedData[index] = {...updatedData[index], isDeleted: true};
                                            setSelectedDataRow(prev => ({
                                                ...prev,
                                                annexes: updatedData
                                            }));
                                        }
                                        // }
                                    }}
                                >
                                    <Editing
                                        mode="cell"
                                        allowUpdating={mode !== Mode.Lecture}
                                        allowDeleting={mode !== Mode.Lecture}
                                        allowAdding={mode !== Mode.Lecture}
                                        newRowPosition={"first"}
                                        startEditAction={"click"}
                                    />
                                    <Column caption={"Section à mettre en annexe"}/>
                                    <Column caption={"Description"}/>
                                    <Column dataField="isDeleted" dataType="boolean" visible={false}/>
                                </DataGrid>
                            </GroupItem>
                        </GroupItem>
                    </Form>
                </TabItem>
                <TabItem title="Système"><FormSysteme formData={selectedGroupe || {} as IEtatFinancierGroupe}/></TabItem>
            </FormListBaseTab>
        </FormListBase>
    );
}